import { useRef } from 'react';

import { delayedCallback } from '@gem/utils';
import type { IDelayedTimeout } from '@gem/utils';

import type { IAsyncValidationParams, ValidationResult } from './hooks.types';

function runValidations({
  value,
  values,
  validate,
  asyncValidate,
  errorToText,
  asyncTimeout,
  data,
  meta,
}: IAsyncValidationParams & { asyncTimeout: IDelayedTimeout }): ValidationResult {
  const error = validate({
    value,
    values,
    errorToText,
    data,
  });

  if (error) {
    return error;
  }

  // we don't need to do async validation
  // if value is equal to init value
  if (meta.pristine) {
    return null;
  }

  return delayedCallback(asyncTimeout, () => {
    return asyncValidate({
      value,
      values,
      errorToText,
      data,
    });
  });
}

export const useAsyncValidation = (): ((params: IAsyncValidationParams) => ValidationResult) => {
  const timeout = useRef<IDelayedTimeout['current']>(null);
  const asyncTimeout = useRef<IDelayedTimeout['current']>(null);

  return (params: IAsyncValidationParams): ValidationResult => {
    // we need to async validate at the first time typing
    return delayedCallback(timeout, () => {
      return runValidations({
        ...params,
        asyncTimeout: asyncTimeout,
      });
    });
  };
};
